import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UnsavedWorkDialog } from "@/components/UnsavedWorkDialog";
import { useDraft, useLeaveWarning } from "@/lib/useDraft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GitBranch, AlertTriangle } from "lucide-react";
import { ScanField } from "@/components/ScanField";
import { resolveScannedTag, tagStatusWords } from "@/lib/tagScan";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { ReferencePicker, buildHandoffPayload, isProtectedNumber } from "@/components/ReferencePicker";
import { ChoiceButton } from "@/components/ChoiceButton";

// Spin a component that lives ON an assembly out into its OWN child part, MOVING its
// identifying references (numbers + keywords) off the parent so a search for the
// component stops landing on the parent. move != copy; the assembly's own identity is guarded.
export function SpinOffChildDialog({ open, onOpenChange, part, initialGroupId, onDone }) {
  const numbers = useMemo(() => part?.numbers || [], [part]);
  const keywords = useMemo(() => part?.keywords || [], [part]);
  const primaryNorm = part?.oem_normalized || null;
  const supersededNorms = useMemo(() => part?.superseded_normalized || [], [part]);

  const [sel, setSel] = useState(new Set());
  const [selKw, setSelKw] = useState(new Set());
  const [keepKw, setKeepKw] = useState(new Set());
  const [tag, setTag] = useState("");
  const [label, setLabel] = useState("");
  const [brand, setBrand] = useState("");
  const [waitingName, setWaitingName] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  // TYPED WORK IS WORK (2026-10-15): a tag, a name or a note. Pre-ticked references are not — they
  // were the system's suggestion, not something a person sat and entered.
  const started = !!(tag.trim() || label.trim() || note.trim());
  const draft = useDraft({
    key: `spinoff:${part?.id || part?._id || "none"}`,
    enabled: open,
    snapshot: () => ({ tag, label, note }),
    restore: (d) => { setTag(d.tag || ""); setLabel(d.label || ""); setNote(d.note || ""); },
    isWorth: (d) => !!((d?.tag || "").trim() || (d?.label || "").trim() || (d?.note || "").trim()),
  });
  useLeaveWarning(open && started);
  const close = (force = false) => {
    if (!force && started) { setAskDiscard(true); return; }
    onOpenChange(false);
  };
  const discard = () => {
    draft.clear(); setTag(""); setLabel(""); setNote("");
    setAskDiscard(false); onOpenChange(false);
  };

  useEffect(() => {
    if (!open) return;
    // THE WHOLE TAG COMES OFF (his rule, 3 Nov): opened from a tag's heading, every number on that tag is ticked and
    // stays ticked. Never a locked identity/superseded number.
    const onTag = initialGroupId
      ? numbers.filter((n) => n.group_id === initialGroupId && !isProtectedNumber(n, primaryNorm, part?.oem_number, supersededNorms))
      : [];
    setSel(new Set(onTag.map((n) => n.id)));
    // THE COMPONENT'S OWN SEARCH WORDS COME WITH IT (his instruction, 2026-10-14) — the words recorded against that same
    // tag are ticked; the assembly's own words are never ticked, and every tick is still the person's to undo.
    const grp = onTag.length ? initialGroupId : null;
    const ownWords = grp
      ? (part?.keyword_sources || []).filter((s) => s.group_id === grp).map((s) => s.kw)
          .filter((k) => keywords.some((x) => String(x).toLowerCase() === String(k).toLowerCase()))
      : [];
    setSelKw(new Set(ownWords)); setKeepKw(new Set());
    setTag(""); setLabel("");
    // THE CHOSEN TAG HEADING CARRIES OVER (his call, 21 Oct): a heading that is a name on our list becomes the new
    // part's starting name. The AI's own words and "Tag 2" are captions, so they never do.
    // THE TAG'S BRAND CARRIES OVER TOO (his call, 23 Oct), and so does a name waiting for a list admin's approval —
    // the child is renamed with the tags if that name is merged into another.
    const first = onTag[0] || {};
    const heading = (first.group_label || "").trim();
    setBrand(first.group_id ? (first.brand || "") : ""); setWaitingName(false);
    if (heading) {
      const key = heading.toLowerCase().split(/\s+/).join(" ");
      api.get("/managed-lists/names", { params: { q: heading } })
        .then(({ data }) => {
          const hit = (data.items || []).find((x) => x.name_normalized === key);
          if (hit) { setLabel((cur) => cur || hit.name); return; }
          api.get("/name-suggestions/check", { params: { name: heading } })
            .then(({ data: w }) => { if (w.waiting) { setLabel((cur) => cur || w.name); setWaitingName(true); } }).catch(() => {});
        })
        .catch(() => {});
    }
    setNote("Component pulled off the assembly and made its own part.");
  }, [open, initialGroupId, numbers, keywords, primaryNorm, supersededNorms, part]);

  const toggle = (setFn, val) => setFn((prev) => { const n = new Set(prev); n.has(val) ? n.delete(val) : n.add(val); return n; });

  const flagged = useMemo(() => {
    const hay = `${part?.name || ""} ${part?.notes || ""}`.toLowerCase();
    const terms = [
      ...numbers.filter((n) => sel.has(n.id)).map((n) => n.value),
      ...[...selKw],
    ].map((t) => String(t || "").toLowerCase()).filter((t) => t.length >= 3);
    return [...new Set(terms.filter((t) => hay.includes(t)))];
  }, [sel, selKw, part, numbers]);

  const submit = async () => {
    if (!tag.trim()) { toast.error("Scan or type a tag for the child part."); return; }
    if (!note.trim()) { toast.error("Add a note recording what left the parent."); return; }
    if (sel.size === 0) { toast.error("Tick the bolt-on tag that comes off — a tag comes off whole."); return; }
    const payload = { tag_code: tag.trim(), label: label.trim(), brand, note: note.trim(), ...buildHandoffPayload(sel, selKw, keepKw) };
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${part.id}/spin-off-child`, payload);
      toast.success(`Spun off child ${data.child_tag} — ${data.moved_numbers} number(s) & ${data.moved_keywords} keyword(s) moved off this assembly.`);
      draft.clear();
      onOpenChange(false);
      onDone?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent data-testid="spinoff-dialog" className="sm:max-w-lg max-h-kb overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch className="w-4 h-4 text-[#B01E28]" />Spin off a component into its own part</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Move a component's number(s) and keyword(s) off this assembly onto a new child part, so a search for the component stops landing on the assembly.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-xs text-slate-500">{initialGroupId ? "This tag comes off whole — its name and every number on it." : "Tick the bolt-on tag that comes off — a tag comes off whole."} They <b>move off the assembly</b> and onto the new child part. The child lands in Identify to be graded/named. Donor tie carries over unchanged.</p>

          <ReferencePicker
            numbers={numbers} keywords={keywords} primaryNorm={primaryNorm} primaryValue={part?.oem_number} supersededNorms={supersededNorms} prefix="spinoff"
            lockGroup={initialGroupId || null} ownGroup={part?.assembly_own_tag?.group_id || null}
            sel={sel} onToggleNum={(id) => toggle(setSel, id)}
            selKw={selKw} onToggleKw={(k) => toggle(setSelKw, k)}
            keepKw={keepKw} onToggleKeepKw={(k) => toggle(setKeepKw, k)}
          />

          {/* Free-text flag */}
          {flagged.length > 0 && (
            <div data-testid="spinoff-freetext-flag" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>The assembly's name/notes mention <b>{flagged.join(", ")}</b>. I won't auto-edit your text — after this, edit the assembly's name/notes so a search doesn't still surface it.</span>
            </div>
          )}

          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Child tag (required)</label>
            <div className="mt-1">
              <ScanField testid="spinoff-tag" placeholder="Scan gun · camera · or type tag…" submitLabel="Set tag"
                onScan={async (c) => {
                  const r = await resolveScannedTag(c);
                  if (!r.code) { scanRefuse(r.error || "COULDN'T READ THAT TAG"); toast.error(r.error || "Couldn't read that tag."); return; }
                  if (r.boundPart || r.status === "voided" || r.status === "retired") {
                    scanRefuse(tagStatusWords(r)); toast.error(tagStatusWords(r)); return;
                  }
                  scanAccept(r.code);
                  setTag(r.code);
                }} autoFocus={false} uppercase />
            </div>
            {tag
              ? <div className="mt-1.5 flex items-center gap-2 text-xs" data-testid="spinoff-tag-value"><span className="text-slate-500">Child tag:</span><span className="font-mono font-bold text-[#B01E28]">{tag}</span><ChoiceButton data-testid="spinoff-tag-change" onClick={() => setTag("")} className="h-7 min-h-7 px-2">Change</ChoiceButton></div>
              : <p className="mt-1 text-[11px] text-slate-400">Scan on a tablet/phone camera or a scan gun — or type the tag and press Set.</p>}
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Short label (optional)</label>
            <Input data-testid="spinoff-label" value={label} onChange={(e) => { setLabel(e.target.value); setWaitingName(false); }} placeholder="e.g. Doser" className="rounded-lg h-10 mt-1" />
            {waitingName && <p data-testid="spinoff-label-waiting" className="text-[11px] text-amber-800 mt-1">This name is waiting for a list admin's approval. If it's merged into another name, this part is renamed with it.</p>}
            {brand && (
              <div data-testid="spinoff-brand" className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                Brand from the tag: <b>{brand}</b>
                <ChoiceButton data-testid="spinoff-brand-clear" onClick={() => setBrand("")} className="h-7 min-h-7 px-2">No brand</ChoiceButton>
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Note on the parent (required)</label>
            <Textarea data-testid="spinoff-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-lg mt-1 text-sm" />
            <p className="text-[11px] text-slate-400 mt-1">The child's tag and a link back to it are recorded automatically on both records.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close()} className="rounded-lg">Cancel</Button>
          <Button data-testid="spinoff-submit" onClick={submit} disabled={busy} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white">
            <GitBranch className="w-4 h-4 mr-1.5" />{busy ? "Spinning off…" : "Create child & move references"}
          </Button>
        </DialogFooter>
        <UnsavedWorkDialog open={askDiscard} onOpenChange={setAskDiscard} count={1}
          noun="component you were splitting off" onDiscard={discard} testid="spinoff-unsaved" />
      </DialogContent>
    </Dialog>
  );
}
